import { useEffect, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { firestore } from './firebaseConfig';

/**
 * Module-level cache so multiple components mounting in the same session
 * don't each hit Firestore. The list is tiny (≈30 docs) and rarely changes,
 * so a fetch-once-per-page-load cache is enough.
 */
let cache = null;
let inflight = null;

async function fetchCompanies() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const q = query(collection(firestore, 'companies'), orderBy('id', 'asc'));
      const snap = await getDocs(q);
      cache = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name || '',
          nip: data.nip || '',
          address: data.address || '',
          accounts: data.accounts || [],
        };
      });
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Hook: returns the list of companies (spółki) stored in Firestore.
 * Falls back to an empty list if the fetch fails.
 */
export default function useCompanies() {
  const [companies, setCompanies] = useState(cache || []);
  const [loading, setLoading] = useState(!cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cache) return undefined;
    let mounted = true;
    fetchCompanies()
      .then((list) => {
        if (!mounted) return;
        setCompanies(list);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        console.error('useCompanies: failed to load companies', err);
        setError(err);
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return { companies, loading, error };
}
